import { BenchmarkCategory, IndividualDiseaseRiskCategoryFreeName } from '@/api/generated-catch-backend/model';
import { RiskBadge, getRiskBadgeColor, getRiskText } from '@/app/components/common';
import NextArrow from '@/app/components/questionnaireV2/icons/next-arrow';
import MarkdownTextTailwind from '@/components/markdown/markdown-text-tailwind';
import { snakeCaseToTitleCase } from '@/utils/common/snakeCaseToTitleCase';

interface TopRisksCardProps {
  // Core data
  disease: string;
  diseaseSlug: string;
  riskValue: number;
  benchmark: BenchmarkCategory;
  populationAverage: number;
  categoryFreeName?: IndividualDiseaseRiskCategoryFreeName;

  // Layout variants
  variant?: 'fixed' | 'responsive';
  badgePosition?: 'inline' | 'responsive';

  // Interaction
  onClick?: (disease: string) => void;
  showArrow?: boolean;

  // Styling
  className?: string;
  testId?: string;
}

export const TopRisksCard = ({
  disease,
  riskValue,
  benchmark,
  populationAverage,
  diseaseSlug,
  variant = 'responsive',
  badgePosition = 'responsive',
  onClick,
  showArrow = false,
  className = '',
  testId = 'top-risks-card',
}: TopRisksCardProps) => {
  const isClickable = !!onClick;
  const isFixed = variant === 'fixed';
  const isBadgeInline = badgePosition === 'inline';

  const handleClick = isClickable
    ? (e: React.MouseEvent) => {
        e.stopPropagation();
        onClick(diseaseSlug);
      }
    : undefined;

  const baseClasses = `
    group relative flex items-center justify-between rounded-lg bg-white p-5
    ${isClickable ? 'cursor-pointer hover:-translate-y-px hover:transform active:translate-y-0 active:transform' : 'hover:-translate-y-px hover:transform active:translate-y-0 active:transform'}
    ${isFixed ? 'h-[88px] w-[295px]' : 'h-full w-full'}
    ${className}
  `.trim();

  return (
    <div className={baseClasses} onClick={handleClick} data-testid={testId} data-disease={disease}>
      <div className={`flex-1 ${isFixed ? 'h-12' : ''}`}>
        <div className={`flex flex-col justify-center gap-1 ${isFixed ? 'h-12' : ''}`}>
          {/* Desktop badge (responsive positioning only) */}
          {!isBadgeInline && (
            <div className="hidden pb-2 md:flex">
              <RiskBadge value={riskValue} colorClass={getRiskBadgeColor(benchmark)} badgeType="risk" />
            </div>
          )}

          {/* Title row with conditional badge */}
          <div className="flex items-center gap-2 text-left">
            <h3 className="body-large font-bold whitespace-nowrap">{snakeCaseToTitleCase(disease)}</h3>

            {/* Inline badge or mobile badge for responsive */}
            <div className={`flex items-center gap-[5px] ${!isBadgeInline ? 'md:hidden' : ''}`}>
              <RiskBadge value={riskValue} colorClass={getRiskBadgeColor(benchmark)} badgeType="risk" />
            </div>
          </div>

          {/* Progress row */}
          <div className="flex items-center gap-2">
            <span
              className={`body-small text-primary leading-[1.3] ${isFixed ? 'text-left' : 'md:text-left md:whitespace-pre-line'}`}
            >
              <MarkdownTextTailwind
                components={{
                  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                }}
              >
                {getRiskText(benchmark, populationAverage)}
              </MarkdownTextTailwind>
            </span>
          </div>
        </div>
      </div>

      {/* Arrow button (optional) */}
      {showArrow && (
        <div className="md:absolute md:top-3 md:right-3" onClick={handleClick}>
          <div className="flex h-[39px] w-[39px] flex-shrink-0 items-center justify-center rounded-full bg-[#f0f5f6] p-3 text-[#183344] transition-colors duration-200 ease-in-out group-hover:bg-[#183344] group-hover:text-white">
            <NextArrow />
          </div>
        </div>
      )}
    </div>
  );
};
